import { toast as sonnerToast } from 'sonner'

import { ApiError } from '@/api'

export type ToastKind = 'error' | 'success' | 'info' | 'warning'

export type ToastAction = {
  label: string
  onClick: () => void
}

export type ToastItem = {
  id: string
  kind: ToastKind
  message: string
  action?: ToastAction
}

const recentKeys = new Map<string, number>()

const DEDUPE_MS = 4000
const DEFAULT_DURATION: Record<ToastKind, number> = {
  error: 6000,
  warning: 5000,
  success: 3500,
  info: 4000,
}

function dedupeKey(kind: ToastKind, message: string): string {
  return `${kind}:${message}`
}

function shouldSkipDedupe(key: string): boolean {
  const last = recentKeys.get(key)
  const now = Date.now()
  if (last != null && now - last < DEDUPE_MS) return true
  recentKeys.set(key, now)
  return false
}

/** @deprecated Sonner 管理 toast 列表；保留 API 兼容 */
export function subscribeToasts(_listener: () => void): () => void {
  return () => {}
}

/** @deprecated Sonner 管理 toast 列表；保留 API 兼容 */
export function getToasts(): readonly ToastItem[] {
  return []
}

/** @deprecated 使用 sonner.dismiss */
export function dismissToast(id: string): void {
  sonnerToast.dismiss(id)
}

export function notify(
  kind: ToastKind,
  message: string,
  opts?: {
    action?: ToastAction
    duration?: number
    dedupe?: boolean
  },
): void {
  const trimmed = message.trim()
  if (!trimmed) return

  const key = dedupeKey(kind, trimmed)
  if (opts?.dedupe !== false && shouldSkipDedupe(key)) return

  const duration = opts?.duration ?? DEFAULT_DURATION[kind]
  const action = opts?.action
    ? {
        label: opts.action.label,
        onClick: opts.action.onClick,
      }
    : undefined

  switch (kind) {
    case 'error':
      sonnerToast.error(trimmed, { duration, action })
      break
    case 'success':
      sonnerToast.success(trimmed, { duration, action })
      break
    case 'warning':
      sonnerToast.warning(trimmed, { duration, action })
      break
    default:
      sonnerToast.info(trimmed, { duration, action })
  }
}

export const toast = {
  error: (message: string, opts?: Parameters<typeof notify>[2]) =>
    notify('error', message, opts),
  success: (message: string, opts?: Parameters<typeof notify>[2]) =>
    notify('success', message, opts),
  info: (message: string, opts?: Parameters<typeof notify>[2]) =>
    notify('info', message, opts),
  warning: (message: string, opts?: Parameters<typeof notify>[2]) =>
    notify('warning', message, opts),
}

export function apiErrorMessage(err: unknown, fallback = 'request failed'): string {
  if (err instanceof ApiError) return err.message
  if (err instanceof Error) return err.message
  if (typeof err === 'string') return err
  return fallback
}

export function notifyApiError(
  err: unknown,
  opts?: Parameters<typeof notify>[2] & { fallback?: string },
): void {
  toast.error(apiErrorMessage(err, opts?.fallback), opts)
}
